<template>
    <section class="flex flex-col gap-5">
        <header class="flex flex-col gap-2">
            <h2 class="title-subsection text-valence-stone-900">Reflections Settings</h2>
            <p class="body-small text-valence-stone-500">Organization-wide settings for every reflection in your organization, including campaigns already in flight.</p>
        </header>

        <hr class="border-t border-valence-cream-300" />

        <button type="button" class="body-small text-valence-stone-500 hover:text-valence-stone-900 flex items-center gap-1 self-start" @click="emit('back')">
            <i class="bi bi-chevron-left" aria-hidden="true"></i> Back
        </button>

        <div v-if="loading" class="body-small text-valence-stone-500">Loading…</div>
        <BaseNotification v-else-if="loadError" theme="error" :text="loadError" hide-dismiss />

        <template v-else>
            <BaseCard padding="lg">
                <div class="flex flex-col gap-6">
                    <div class="scaffold-details-small text-valence-stone-500">Rater privacy</div>
                    <label for="anonymize-rater-status" class="flex items-start justify-between gap-6 cursor-pointer">
                        <span class="flex flex-col gap-0.5">
                            <span class="body-small-bold text-valence-stone-900">Anonymize rater status</span>
                            <span class="body-small text-valence-stone-500">
                                Participants still see who they invited and how many raters have responded, but not which ones. They can still nominate raters, remove them, and
                                send reminders.
                            </span>
                        </span>
                        <BaseSwitch id="anonymize-rater-status" v-model="anonymizeRaterStatus" size="md" class="mt-0.5" />
                    </label>
                </div>
            </BaseCard>

            <BaseCard padding="lg">
                <div class="flex flex-col gap-6">
                    <div class="scaffold-details-small text-valence-stone-500">Rater nomination</div>
                    <label for="subject-specifies-relationship" class="flex items-start justify-between gap-6 cursor-pointer">
                        <span class="flex flex-col gap-0.5">
                            <span class="body-small-bold text-valence-stone-900">Participants categorize their raters</span>
                            <span class="body-small text-valence-stone-500">
                                Participants pick each rater's relationship to them (peer, manager, direct report, etc.) when they invite them, and raters skip that step. When off,
                                each rater categorizes themselves instead.
                            </span>
                        </span>
                        <BaseSwitch id="subject-specifies-relationship" v-model="subjectSpecifiesRelationship" size="md" class="mt-0.5" />
                    </label>

                    <label for="allow-any-rater-email-domain" class="flex items-start justify-between gap-6 cursor-pointer">
                        <span class="flex flex-col gap-0.5">
                            <span class="body-small-bold text-valence-stone-900">Allow raters outside your organization</span>
                            <span class="body-small text-valence-stone-500">
                                Participants can invite any email address, such as a customer or an external partner. When off, an invited email address has to be on one of your
                                organization's registered domains.
                            </span>
                        </span>
                        <BaseSwitch id="allow-any-rater-email-domain" v-model="allowAnyRaterEmailDomain" size="md" class="mt-0.5" />
                    </label>
                </div>
            </BaseCard>

            <BaseCard padding="lg">
                <div class="flex flex-col gap-6">
                    <div class="flex flex-col gap-0.5">
                        <div class="scaffold-details-small text-valence-stone-500">Rater categories</div>
                        <p class="body-small text-valence-stone-500">
                            Choose which relationship categories raters are sorted into and how each one is labeled in nomination, surveys, and reports.
                        </p>
                    </div>
                    <div class="flex flex-col gap-5 sm:gap-3">
                        <div class="hidden sm:flex items-center gap-4 scaffold-details-small text-valence-stone-500">
                            <span class="w-9 shrink-0">On</span>
                            <span class="w-40 shrink-0">Category</span>
                            <span>Label shown to users</span>
                        </div>
                        <!-- Stacked on mobile; `sm:contents` dissolves the switch/name pair
                             into the row so the three columns line up on wider screens. -->
                        <div v-for="row in relationshipRows" :key="row.value" class="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-4">
                            <div class="flex items-center gap-3 sm:contents">
                                <div class="sm:w-9 sm:shrink-0">
                                    <BaseSwitch :id="`relationship-${row.value}`" v-model="row.active" size="md" :aria-label="`Enable ${row.defaultLabel}`" />
                                </div>
                                <span class="body-small sm:w-40 sm:shrink-0" :class="row.active ? 'text-valence-stone-900' : 'text-valence-stone-500'">
                                    {{ row.defaultLabel }}
                                </span>
                            </div>
                            <input
                                v-model="row.label"
                                type="text"
                                maxlength="64"
                                :placeholder="row.defaultLabel"
                                :disabled="!row.active"
                                :aria-label="`Label for ${row.defaultLabel}`"
                                class="body-small text-valence-stone-900 border border-valence-cream-300 rounded-lgr px-3 py-1.5 w-full sm:w-64 disabled:text-valence-stone-500 disabled:bg-valence-cream-100" />
                        </div>
                    </div>
                    <BaseNotification v-if="relationshipsError" theme="error" :text="relationshipsError" hide-dismiss />
                </div>
            </BaseCard>

            <BaseNotification v-if="saveStatus" :theme="saveStatus.theme" :text="saveStatus.text" class="mb-2" @dismiss="saveStatus = null" />

            <div class="flex items-center justify-end gap-3">
                <BaseButton theme="neutral" size="sm" class="base-btn-square" :disabled="saving || !isDirty" @click="reset">Cancel</BaseButton>
                <!-- LAB-988 dark CTA override — see .base-btn-dark in tailwind.css. -->
                <BaseButton theme="primary" size="sm" class="base-btn-dark" :disabled="saving || !isDirty || !!relationshipsError" @click="showSaveConfirm = true">
                    {{ saving ? "Saving…" : "Save settings" }}
                </BaseButton>
            </div>

            <SubmitConfirmDialog
                v-model:open="showSaveConfirm"
                title="Update reflections settings?"
                confirm-label="Update settings"
                cancel-label="Cancel"
                :confirm-disabled="saving"
                @confirm="save">
                <p class="body text-valence-stone-500">
                    This will update the setting for all assessments in your organization, including already active ones. Are you sure you want to update these settings?
                </p>
            </SubmitConfirmDialog>
        </template>
    </section>
</template>

<script setup lang="ts">
import { useFetch } from "~vue/api";
import type { AssessmentAdminSettingsOutput, RaterRelationshipOption } from "~vue/api/generated/reflections-admin";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseCard from "~vue/components/BaseCard.vue";
import BaseNotification from "~vue/components/BaseNotification.vue";
import BaseSwitch from "~vue/components/BaseSwitch.vue";
import SubmitConfirmDialog from "~vue/pages/reflections/SubmitConfirmDialog.vue";
import { computed, onMounted, ref } from "vue";

const emit = defineEmits<{
    (event: "back"): void;
}>();

const loading = ref(true);
const loadError = ref<string | null>(null);
const saving = ref(false);
const saveStatus = ref<{ theme: "success" | "error"; text: string } | null>(null);
const showSaveConfirm = ref(false);

interface RelationshipRow {
    value: RaterRelationshipOption["value"];
    defaultLabel: string;
    label: string;
    active: boolean;
}

const anonymizeRaterStatus = ref(false);
const subjectSpecifiesRelationship = ref(false);
const allowAnyRaterEmailDomain = ref(false);
const relationshipRows = ref<RelationshipRow[]>([]);
const saved = ref<AssessmentAdminSettingsOutput>({ anonymize_rater_status: false, subject_specifies_relationship: false, allow_any_rater_email_domain: false });

const selectedRelationships = computed<RaterRelationshipOption[]>(() =>
    relationshipRows.value.filter((row) => row.active).map((row) => ({ value: row.value, label: row.label.trim() || row.defaultLabel })),
);

const relationshipsError = computed(() => (relationshipRows.value.some((row) => row.active) ? null : "At least one rater category must be active."));

const isDirty = computed(
    () =>
        anonymizeRaterStatus.value !== (saved.value.anonymize_rater_status ?? false) ||
        subjectSpecifiesRelationship.value !== (saved.value.subject_specifies_relationship ?? false) ||
        allowAnyRaterEmailDomain.value !== (saved.value.allow_any_rater_email_domain ?? false) ||
        JSON.stringify(selectedRelationships.value) !== JSON.stringify(saved.value.relationships ?? []),
);

function apply(settings: AssessmentAdminSettingsOutput) {
    saved.value = settings;
    anonymizeRaterStatus.value = settings.anonymize_rater_status ?? false;
    subjectSpecifiesRelationship.value = settings.subject_specifies_relationship ?? false;
    allowAnyRaterEmailDomain.value = settings.allow_any_rater_email_domain ?? false;
    const activeByValue = new Map((settings.relationships ?? []).map((option) => [option.value, option.label]));
    relationshipRows.value = (settings.available_relationships ?? []).map((option) => ({
        value: option.value,
        defaultLabel: option.label,
        label: activeByValue.get(option.value) ?? option.label,
        active: activeByValue.has(option.value),
    }));
}

function reset() {
    apply(saved.value);
    saveStatus.value = null;
}

onMounted(async () => {
    const { data, error: fetchError } = await useFetch("/v1/reflections/admin/settings").json<AssessmentAdminSettingsOutput>();
    loading.value = false;
    if (fetchError.value) {
        loadError.value = fetchError.value.message || "Could not load settings.";
        return;
    }
    if (data.value) {
        apply(data.value);
    }
});

async function save() {
    if (saving.value || !isDirty.value || relationshipsError.value) {
        showSaveConfirm.value = false;
        return;
    }
    saving.value = true;
    saveStatus.value = null;
    const body: AssessmentAdminSettingsOutput = {
        anonymize_rater_status: anonymizeRaterStatus.value,
        subject_specifies_relationship: subjectSpecifiesRelationship.value,
        allow_any_rater_email_domain: allowAnyRaterEmailDomain.value,
        relationships: selectedRelationships.value,
    };
    const { data, error: fetchError } = await useFetch("/v1/reflections/admin/settings").put(JSON.stringify(body), "application/json").json<AssessmentAdminSettingsOutput>();
    saving.value = false;
    showSaveConfirm.value = false;
    if (fetchError.value || !data.value) {
        saveStatus.value = { theme: "error", text: "Could not save settings. Please try again." };
        return;
    }
    apply(data.value);
    saveStatus.value = { theme: "success", text: "Settings saved." };
}
</script>
